import React, { useState } from 'react';
import { Reorder, useDragControls } from 'framer-motion';
import { GripVerticalIcon } from 'lucide-react';
import { usePortal } from '../../contexts/PortalContext';
import { DELIVERABLE_STAGES, DELIVERABLE_STATUSES, DELIVERABLE_TYPES } from '../../types/portal';
import type { Deliverable } from '../../types/portal';
import { metaLabel, stageHeading } from '../../utils/deliverables';
import { VersionHistory } from './VersionHistory';
import { TranscriptUpload } from './TranscriptUpload';
import { TranscriptRevisions } from './TranscriptRevisions';
import { isDriveFolder, toDrivePreviewUrl } from '../../utils/drive';
import { BUTTON_GHOST, BUTTON_QUIET, BUTTON_SOLID, FIELD, OPTION, SELECT } from './adminStyles';
interface DeliverableAdminProps {
  deliverable: Deliverable;
  /** Reports which row is currently lifted, or null once it is set down. */
  onLift: (id: string | null) => void;
}
export function DeliverableAdmin({
  deliverable,
  onLift
}: DeliverableAdminProps) {
  const dragControls = useDragControls();
  const [carrying, setCarrying] = useState(false);
  const {
    updateDeliverable,
    removeDeliverable,
    publishVersion,
    resolveChangeRequest,
    notify,
    focusDeliverableId
  } = usePortal();

  /** 'details' shows the client feedback; 'edit' also shows the configuration. */
  const [panel, setPanel] = useState<'closed' | 'details' | 'edit'>(focusDeliverableId === deliverable.id ? 'details' : 'closed');
  const open = panel !== 'closed';
  const editingRow = panel === 'edit';
  const setOpen = (next: boolean) => setPanel(next ? 'edit' : 'closed');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [draft, setDraft] = useState<Deliverable>(deliverable);
  function edit(patch: Partial<Deliverable>) {
    setDraft(current => ({
      ...current,
      ...patch
    }));
  }
  function save() {
    updateDeliverable(deliverable.id, {
      name: draft.name.trim() || deliverable.name,
      type: draft.type,
      quantity: draft.quantity,
      stage: draft.stage,
      status: draft.status,
      version: draft.version,
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl,
      internalNote: draft.internalNote,
      visibleToClient: draft.visibleToClient,
      transcript: draft.transcript
    });
    notify(`${draft.name || deliverable.name} · client view updated`);
    setOpen(false);
  }
  function publish() {
    updateDeliverable(deliverable.id, {
      driveUrl: draft.driveUrl,
      audioUrl: draft.audioUrl,
      stage: 'Review'
    });
    publishVersion(deliverable.id, draft.version);
    notify(`${deliverable.name} · ${draft.version || 'new version'} published for review`);
  }
  const outstanding = deliverable.changeRequests.filter(request => !request.resolved);
  /** Completed requests stay on the record, newest first. */
  const completed = deliverable.changeRequests.filter(request => request.resolved).reverse();
  return <Reorder.Item value={deliverable} dragListener={false} dragControls={dragControls} onDragStart={() => {
    setCarrying(true);
    onLift(deliverable.id);
  }} onDragEnd={() => {
    setCarrying(false);
    onLift(null);
  }}
  /* The row itself is carried, so it stays fully visible and readable. */ animate={carrying ? {
    backgroundColor: 'rgba(255,255,255,0.05)',
    boxShadow: '0 18px 40px rgba(0,0,0,0.55)'
  } : {
    backgroundColor: 'rgba(255,255,255,0)',
    boxShadow: '0 0 0 rgba(0,0,0,0)'
  }} transition={{
    duration: 0.18,
    ease: [0.23, 1, 0.32, 1]
  }} style={{
    position: 'relative',
    zIndex: carrying ? 30 : 0
  }} className={['border-b border-ruleDark', carrying ? 'cursor-grabbing rounded-[2px] ring-1 ring-paper/25' : ''].join(' ')} data-id="element-226" data-mp="7fbyk0km7jk2">
      <div className="flex flex-wrap items-center gap-x-6 gap-y-4 px-3 py-6" data-id="element-227" data-mp="2tm6yoabo1y3v">
        <span role="button" tabIndex={0} onPointerDown={event => {
        event.preventDefault();
        dragControls.start(event);
      }} aria-label={`Reorder ${deliverable.name}`} title="Drag to reorder" className="shrink-0 touch-none cursor-grab text-paper/30 transition-colors duration-150 ease-out hover:text-paper/70 active:cursor-grabbing" data-id="element-228" data-mp="2opq0enar63d2">
          <GripVerticalIcon className="h-5 w-5" strokeWidth={1.25} data-id="element-229" data-mp="12nt6e4o1fnwh" />
        </span>

        {/* The row itself opens the deliverable's details and client feedback. */}
        <button type="button" onClick={() => setPanel(current => current === 'details' ? 'closed' : 'details')} aria-expanded={open} className="min-w-0 flex-1 text-left" data-id="element-230" data-mp="17ft03fdilkkm">
          <p className="font-display text-2xl leading-none sm:text-3xl" data-id="element-231" data-mp="jp70mchkt8m8">{deliverable.name}</p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-232" data-mp="onqq2mvdp2jc">
            {metaLabel(deliverable)} · {stageHeading(deliverable.stage)} · {deliverable.status}
            {outstanding.length > 0 ? ` · ${outstanding.length} change${outstanding.length === 1 ? '' : 's'} requested` : ''}
          </p>
        </button>

        <button type="button" onClick={() => {
        updateDeliverable(deliverable.id, {
          visibleToClient: !deliverable.visibleToClient
        });
        edit({
          visibleToClient: !deliverable.visibleToClient
        });
      }} aria-pressed={deliverable.visibleToClient} className={['label shrink-0 border px-3 py-2 transition-colors duration-150 ease-out', deliverable.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-233" data-mp="f17dxp0d5pp8">
          {deliverable.visibleToClient ? 'Visible' : 'Hidden'}
        </button>

        <div className="flex shrink-0 items-center gap-6" data-id="element-234" data-mp="12qrygcc60v0m">
          <button type="button" onClick={() => {
          setDraft(deliverable);
          setPanel(current => current === 'edit' ? 'closed' : 'edit');
          setConfirmRemove(false);
        }} className={BUTTON_QUIET} data-id="element-235" data-mp="12o5sui8u4pl1">
            {editingRow ? 'Close' : 'Edit'}
          </button>
          <button type="button" onClick={() => setConfirmRemove(true)} className="label text-paper/40 transition-colors duration-150 ease-out hover:text-paper" data-id="element-236" data-mp="1oj4nha4hs3qw">
            Remove
          </button>
        </div>
      </div>

      {confirmRemove && <div className="mb-6 border border-paper/30 px-6 py-6" data-id="element-237" data-mp="22iw0b7j9ve3b">
          <p className="font-display text-xl leading-snug" data-id="element-238" data-mp="27gp4xw4xg01h">
            Remove {deliverable.name} from the dashboard?
          </p>
          <p className="label-sm mt-3 text-paper/40" data-id="element-239" data-mp="84v0okfwen26">
            This only removes it from the client dashboard. Nothing is deleted from Google Drive.
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-6" data-id="element-240" data-mp="5j55xs80535l">
            <button type="button" onClick={() => {
          removeDeliverable(deliverable.id);
          notify(`${deliverable.name} removed from dashboard`);
        }} className={BUTTON_SOLID} data-id="element-241" data-mp="5lhvork04ht7">
              Remove
            </button>
            <button type="button" onClick={() => setConfirmRemove(false)} className={BUTTON_QUIET} data-id="element-242" data-mp="3my24d7prknzk">
              Cancel
            </button>
          </div>
        </div>}

      {open && <div className="mb-10 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16" data-id="element-243" data-mp="jhbuuiuz3st4">
          {editingRow && <div className="lg:col-span-7" data-id="element-244" data-mp="9l0hrqnk6yxm">
            <label className="block" data-id="element-245" data-mp="9jyv0ox6ko02">
              <span className="label block text-paper/40" data-id="element-246" data-mp="9n33vzllc7x2">Name</span>
              <input value={draft.name} onChange={event => edit({
            name: event.target.value
          })} placeholder="e.g. Event Highlights Film" className={FIELD} data-id="element-247" data-mp="2zb3rek0ury6e" />
            </label>

            <div className="mt-8 grid grid-cols-2 gap-8 sm:grid-cols-4" data-id="element-248" data-mp="3oy6j5igyts8h">
              <label className="block" data-id="element-249" data-mp="3owdci36cr80h">
                <span className="label block text-paper/40" data-id="element-250" data-mp="3azgkvzietmij">Type</span>
                <select value={draft.type} onChange={event => edit({
              type: event.target.value as Deliverable['type']
            })} className={SELECT} data-id="element-251" data-mp="3f0a9kv3of5ch">
                  {DELIVERABLE_TYPES.map(option => <option key={option} value={option} className={OPTION} data-id="element-252" data-mp="bis0kkc63sah">
                      {option}
                    </option>)}
                </select>
              </label>
              <label className="block" data-id="element-253" data-mp="1rxhfou7x8t9">
                <span className="label block text-paper/40" data-id="element-254" data-mp="pjkvh66dspe4">Quantity</span>
                <input value={draft.quantity} onChange={event => edit({
              quantity: event.target.value
            })} placeholder="e.g. 8" className={FIELD} data-id="element-255" data-mp="phhwvxr5xnxs" />
              </label>
              <label className="block" data-id="element-256" data-mp="2a0lqp2eqmnmw">
                <span className="label block text-paper/40" data-id="element-257" data-mp="29zj06vlbun7i">Stage</span>
                <select value={draft.stage} onChange={event => edit({
              stage: event.target.value as Deliverable['stage']
            })} className={SELECT} data-id="element-258" data-mp="2opq7qg9sqltf">
                  {DELIVERABLE_STAGES.map(option => <option key={option} value={option} className={OPTION} data-id="element-259" data-mp="1r3kjrc1midgb">
                      {option}
                    </option>)}
                </select>
              </label>
              {draft.stage === 'Review' || draft.stage === 'Final Delivery' ? <label className="block" data-id="element-260" data-mp="3hefbyo65f79e">
                  <span className="label block text-paper/40" data-id="element-261" data-mp="3hd40s3adix89">Version</span>
                  <input value={draft.version} onChange={event => edit({
              version: event.target.value
            })} placeholder="e.g. V3" className={FIELD} data-id="element-262" data-mp="3cf2hbh91cvw9" />
                </label> : <div className="block" data-id="element-263" data-mp="mmh7k1a1ezog">
                  <span className="label block text-paper/40" data-id="element-264" data-mp="mlfwhwt0ud6q">Version</span>
                  <p className="mt-3 border-b border-paper/25 pb-2 font-sans text-sm tracking-wide2 text-paper/30" data-id="element-265" data-mp="hnwj0z45e4fu">
                    Added at Review
                  </p>
                </div>}
            </div>

            <label className="mt-8 block" data-id="element-266" data-mp="8pu89rum3xfd">
              <span className="label block text-paper/40" data-id="element-267" data-mp="3nv7mpq9bem4">Status</span>
              <select value={draft.status} onChange={event => edit({
            status: event.target.value as Deliverable['status']
          })} className={SELECT} data-id="element-268" data-mp="3uuxzljdx5oxe">
                {DELIVERABLE_STATUSES.map(option => <option key={option} value={option} className={OPTION} data-id="element-269" data-mp="1t4e27xiqiakj">
                    {option}
                  </option>)}
              </select>
            </label>

            {draft.type === 'Transcript' && <TranscriptUpload value={draft.transcript ?? ''} onChange={text => edit({
          transcript: text
        })} data-id="element-270" data-mp="2ad6z59pweras" />}

            <label className="mt-8 block" data-id="element-271" data-mp="2fe33foe5wfgq">
              <span className="label block text-paper/40" data-id="element-272" data-mp="1hp1e0luxn0af">Google Drive Link</span>
              <input value={draft.driveUrl} onChange={event => edit({
            driveUrl: event.target.value
          })} placeholder="Paste Drive file or folder link" className={FIELD} data-id="element-273" data-mp="1mn2ssulbsdg3" />
              {draft.driveUrl.trim() && <span className="label-sm mt-3 block text-paper/30" data-id="element-274" data-mp="331uobgdrq0tm">
                  {toDrivePreviewUrl(draft.driveUrl) ? `Embeds as ${toDrivePreviewUrl(draft.driveUrl)}` : isDriveFolder(draft.driveUrl) ? 'Folder link · opens in Drive, cannot play inside Portal' : 'Link not recognised as a Drive file — the client will see a preview-unavailable message'}
                </span>}
            </label>

            <label className="mt-8 block" data-id="element-275" data-mp="1cxsgv6kdkdt5">
              <span className="label block text-paper/40" data-id="element-276" data-mp="1cyu3876ucmq6">Drive Audio Link (optional)</span>
              <input value={draft.audioUrl} onChange={event => edit({
            audioUrl: event.target.value
          })} placeholder="Paste Drive audio link" className={FIELD} data-id="element-277" data-mp="p8q1649awv1a" />
            </label>

            <label className="mt-8 block" data-id="element-278" data-mp="t9s2cqms3h5w">
              <span className="label block text-paper/40" data-id="element-279" data-mp="t8qgrs84zc9x">Internal Note</span>
              <input value={draft.internalNote} onChange={event => edit({
            internalNote: event.target.value
          })} placeholder="Portal only — never shown to the client" className={FIELD} data-id="element-280" data-mp="5j5t4w11fqub" />
            </label>

            <label className="mt-8 flex items-center justify-between gap-6 border-b border-paper/25 pb-3" data-id="element-281" data-mp="3sb1l8y6fu3m0">
              <span className="label text-paper/40" data-id="element-282" data-mp="3dmeve2j6ptgj">Visible to Client</span>
              <button type="button" onClick={() => edit({
            visibleToClient: !draft.visibleToClient
          })} aria-pressed={draft.visibleToClient} className={['label border px-3 py-2 transition-colors duration-150 ease-out', draft.visibleToClient ? 'border-paper bg-paper text-ink' : 'border-paper/30 text-paper/40 hover:border-paper/60'].join(' ')} data-id="element-283" data-mp="3ikq76it1ie1e">
                {draft.visibleToClient ? 'On' : 'Off'}
              </button>
            </label>

            <div className="mt-10 flex flex-wrap items-center gap-5" data-id="element-284" data-mp="2pwkk3peikcye">
              <button type="button" onClick={save} className={BUTTON_SOLID} data-id="element-285" data-mp="2ptonc4eejubj">
                Update Client View
              </button>
              {draft.stage === 'Review' && <button type="button" onClick={publish} className={BUTTON_GHOST} data-id="element-286" data-mp="31b5y2n4lk8hl">
                  Publish New Version
                </button>}
            </div>
            <p className="label-sm mt-6 max-w-md leading-relaxed text-paper/30" data-id="element-287" data-mp="2rfm4w8hf3nwj">
              {draft.stage === 'Review' ? 'Publishing sets the client status to Ready for Review, keeps all notes and adds a system note to the conversation.' : 'Move this deliverable to the Review stage to publish a version for the client.'}
            </p>
          </div>}

          <div className={editingRow ? 'lg:col-span-5 lg:border-l lg:border-ruleDark lg:pl-12' : 'lg:col-span-7'} data-id="element-288" data-mp="3a3zmba20ui7l">
            {/* Outstanding feedback leads, so nothing needing action is buried. */}
            {outstanding.length === 0 && (deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-289" data-mp="j7pqg3io82s4" /> : <VersionHistory deliverable={deliverable} data-id="element-290" data-mp="o593j03mu9ih" />)}

            <h4 className={`label border-b border-ruleDark pb-4 text-paper/40 ${outstanding.length > 0 ? '' : 'mt-12'}`} data-id="element-291" data-mp="swhcjv11vsv4">
              Requested Changes
            </h4>
            <ul className="m-0 list-none p-0" data-id="element-292" data-mp="11we6oc67ljxk">
              {outstanding.length === 0 && <li className="py-5 font-display text-lg text-paper/40" data-id="element-293" data-mp="16vz7ks4xw2cx">Nothing outstanding.</li>}
              {outstanding.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-294" data-mp="1bszef59m5953">
                  <span className="label block text-paper" data-id="element-295" data-mp="38pgcvp5lkr4i">
                    {request.timecode ? `${request.timecode} · ` : ''}
                    {request.authorName}
                  </span>
                  <p className="mt-2 font-display text-lg leading-snug" data-id="element-296" data-mp="35majtg32s6hz">{request.body}</p>
                  <div className="mt-3 flex items-center justify-between gap-4" data-id="element-297" data-mp="31m01d3y3jnm8">
                    <span className="label-sm text-paper/35" data-id="element-298" data-mp="3fl82i2dtl5t2">
                      {request.version ? `${request.version} · ` : ''}
                      Sent {request.time || request.submitted}
                    </span>
                    <button type="button" onClick={() => {
                resolveChangeRequest(deliverable.id, request.id);
                notify('Marked as completed');
              }} className="label-sm shrink-0 text-paper/60 underline underline-offset-4 transition-colors duration-150 ease-out hover:text-paper" data-id="element-299" data-mp="3ojbmuvd2jwja">
                      Mark completed
                    </button>
                  </div>
                </li>)}
            </ul>

            {completed.length > 0 && <>
                <h4 className="label mt-12 border-b border-ruleDark pb-4 text-paper/40" data-id="element-300" data-mp="urs1kbb1in82">
                  Completed Changes
                </h4>
                <ul className="m-0 list-none p-0" data-id="element-301" data-mp="gqxtxuvtgqag">
                  {completed.map(request => <li key={request.id} className="border-b border-ruleDark py-5" data-id="element-302" data-mp="lpsk5hw45h4l">
                      <span className="label block text-paper/60" data-id="element-303" data-mp="71xnjqezism8">
                        {request.timecode ? `${request.timecode} · ` : ''}
                        {request.authorName}
                      </span>
                      <p className="mt-2 font-display text-lg leading-snug text-paper/60" data-id="element-304" data-mp="3u86rzrt49q55">
                        {request.body}
                      </p>
                      <span className="label-sm mt-3 block text-paper/35" data-id="element-305" data-mp="3k8zgla5y05ko">
                        {request.version ? `${request.version} · ` : ''}
                        Requested {request.time || request.submitted}
                        {request.completedAt ? ` · Completed ${request.completedAt}` : ' · Completed'}
                        {request.completedBy ? ` by ${request.completedBy}` : ''}
                      </span>
                    </li>)}
                </ul>
              </>}

            {outstanding.length > 0 && <div className="mt-12" data-id="element-306" data-mp="3qs5auzbc7azj">
                {deliverable.type === 'Transcript' ? <TranscriptRevisions deliverable={deliverable} data-id="element-307" data-mp="3ls98t5m79tmm" /> : <VersionHistory deliverable={deliverable} data-id="element-308" data-mp="3gyodjlbzrdr4" />}
              </div>}

          </div>
        </div>}
    </Reorder.Item>;
}